import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Flame, Trophy, Crown, Target, 
  BarChart3, Calendar, Sparkles, Leaf, Settings, Eye, Clock 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Progress = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [badges, setBadges] = useState([]);
  const [visualizationStats, setVisualizationStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch analytics summary
        const analyticsRes = await fetch(`${API_URL}/api/analytics/summary`, {
          credentials: 'include',
        });
        if (analyticsRes.ok) {
          const data = await analyticsRes.json();
          setAnalytics(data);
        }

        // Fetch weekly data
        const weeklyRes = await fetch(`${API_URL}/api/analytics/weekly`, {
          credentials: 'include',
        });
        if (weeklyRes.ok) {
          const data = await weeklyRes.json();
          setWeeklyData(data.days || []);
        }

        // Fetch badges
        const badgesRes = await fetch(`${API_URL}/api/badges`, {
          credentials: 'include',
        });
        if (badgesRes.ok) {
          const data = await badgesRes.json();
          setBadges(data.badges || []);
        }

        // Fetch visualization stats
        const vizRes = await fetch(`${API_URL}/api/visualization/stats`, {
          credentials: 'include',
        });
        if (vizRes.ok) {
          const data = await vizRes.json();
          setVisualizationStats(data);
        }
      } catch (error) {
        toast.error('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  const getBadgeIcon = (icon) => {
    switch (icon) {
      case 'flame':
        return <Flame className="w-6 h-6" strokeWidth={1.5} />;
      case 'crown':
        return <Crown className="w-6 h-6" strokeWidth={1.5} />;
      case 'trophy':
        return <Trophy className="w-6 h-6" strokeWidth={1.5} />;
      default:
        return <Target className="w-6 h-6" strokeWidth={1.5} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E] pb-24" data-testid="progress-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div>
            <p className="text-[#8BAA9E] text-xs tracking-[0.2em] uppercase font-sans mb-1">Analytics</p>
            <h1 className="font-serif text-2xl text-[#DCD3CA]">Your Progress</h1>
          </div>
        </div>
      </header>

      <div className="px-6 space-y-6">
        {/* Main Stats */}
        <div className="grid grid-cols-2 gap-4">
          {/* Current Streak */}
          <div className="bg-[#252528] border border-white/5 rounded-[20px] p-5" data-testid="current-streak-stat">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
              <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">Current Streak</span>
            </div>
            <p className="font-serif text-4xl text-[#C6A75E]">{analytics?.current_streak || 0}</p>
            <p className="text-[#7A7670] text-xs font-sans mt-1">days</p>
          </div>

          {/* Longest Streak */}
          <div className="bg-[#252528] border border-white/5 rounded-[20px] p-5" data-testid="longest-streak-stat">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-[#C5BCB2]" strokeWidth={1.5} />
              <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">Best Streak</span>
            </div>
            <p className="font-serif text-4xl text-[#C5BCB2]">{analytics?.longest_streak || 0}</p>
            <p className="text-[#7A7670] text-xs font-sans mt-1">days</p>
          </div>
        </div>

        {/* Identity Consistency Score */}
        <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="consistency-score">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#8BAA9E]" strokeWidth={1.5} />
              <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">Identity Consistency</span>
            </div>
            <span className="text-[#7A7670] text-xs font-sans">30-day score</span>
          </div>
          
          <div className="flex items-end gap-4">
            <p className="font-serif text-5xl text-[#8BAA9E]">{analytics?.identity_consistency_score || 0}%</p>
            <div className="flex-1 pb-2">
              <div className="h-2 bg-[#1C1C1E] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#8BAA9E] to-[#A3C2B6] transition-all duration-1000"
                  style={{ width: `${analytics?.identity_consistency_score || 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Activity */}
        <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="weekly-activity">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-[#C5BCB2]" strokeWidth={1.5} />
            <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">This Week</span>
          </div>

          <div className="flex items-end justify-between gap-2">
            {weeklyData.map((day, index) => {
              const percentage = day.total > 0 ? (day.completed / day.total) * 100 : 0;
              const isToday = index === weeklyData.length - 1;
              
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                  <div 
                    className="w-full rounded-lg transition-all"
                    style={{ 
                      height: `${Math.max(20, percentage)}px`,
                      maxHeight: '80px',
                      backgroundColor: percentage === 100 
                        ? '#8BAA9E' 
                        : percentage > 0 
                          ? 'rgba(139, 170, 158, 0.4)' 
                          : '#2C2C2E'
                    }}
                  />
                  <span className={`text-xs font-sans ${isToday ? 'text-[#8BAA9E]' : 'text-[#7A7670]'}`}>
                    {day.day}
                  </span>
                  {day.visualization && (
                    <div className="w-2 h-2 rounded-full bg-[#C6A75E]" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-white/5">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-[#8BAA9E]" />
              <span className="text-[#7A7670] text-xs font-sans">All actions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#C6A75E]" />
              <span className="text-[#7A7670] text-xs font-sans">Visualized</span>
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-[#252528] border border-white/5 rounded-[16px] p-4 text-center">
            <p className="font-serif text-2xl text-[#DCD3CA]">{analytics?.weekly_completion_rate || 0}%</p>
            <p className="text-[#7A7670] text-xs font-sans mt-1">Weekly Rate</p>
          </div>
          <div className="bg-[#252528] border border-white/5 rounded-[16px] p-4 text-center">
            <p className="font-serif text-2xl text-[#DCD3CA]">{analytics?.visualization_count || 0}</p>
            <p className="text-[#7A7670] text-xs font-sans mt-1">Visualizations</p>
          </div>
          <div className="bg-[#252528] border border-white/5 rounded-[16px] p-4 text-center" data-testid="breathwork-count-stat">
            <p className="font-serif text-2xl text-[#DCD3CA]">{analytics?.breathwork_count || 0}</p>
            <p className="text-[#7A7670] text-xs font-sans mt-1">Breathwork Sessions</p>
          </div>
        </div>

        {/* Visualization Statistics */}
        {visualizationStats && (visualizationStats.total_sessions > 0 || visualizationStats.total_minutes > 0) && (
          <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="visualization-stats">
            <div className="flex items-center gap-2 mb-6">
              <Eye className="w-5 h-5 text-[#8BAA9E]" strokeWidth={1.5} />
              <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">Visualization Practice</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-[#1C1C1E] rounded-2xl p-4 text-center">
                <p className="font-serif text-3xl text-[#8BAA9E]">{visualizationStats.total_sessions || 0}</p>
                <p className="text-[#7A7670] text-xs font-sans mt-1">Total Sessions</p>
              </div>
              <div className="bg-[#1C1C1E] rounded-2xl p-4 text-center">
                <p className="font-serif text-3xl text-[#8BAA9E]">{visualizationStats.total_hours || 0}</p>
                <p className="text-[#7A7670] text-xs font-sans mt-1">Hours Visualized</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-[#7A7670]">
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                <span className="font-sans">Avg {visualizationStats.average_session_minutes || 0} min/session</span>
              </div>
              {visualizationStats.last_session_date && (
                <span className="text-[#8BAA9E] font-sans">Last: {visualizationStats.last_session_date}</span>
              )}
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="bg-[#252528] border border-white/5 rounded-[24px] p-6" data-testid="badges-section">
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
            <span className="text-[#B5B1AB] text-xs uppercase tracking-wider font-sans">Milestone Badges</span>
          </div>

          <div className="space-y-4">
            {badges.map((badge) => (
              <div 
                key={badge.days}
                className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${
                  badge.earned 
                    ? 'bg-[#1C1C1E] border border-[#C6A75E]/30' 
                    : 'bg-[#1C1C1E]/50 opacity-60'
                }`}
                data-testid={`badge-${badge.days}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  badge.earned ? 'bg-[#C6A75E]/20 text-[#C6A75E]' : 'bg-[#2C2C2E] text-[#7A7670]'
                }`}>
                  {getBadgeIcon(badge.icon)}
                </div>
                <div className="flex-1">
                  <p className={`font-serif text-lg ${badge.earned ? 'text-[#DCD3CA]' : 'text-[#B5B1AB]'}`}>
                    {badge.name}
                  </p>
                  <p className="text-[#7A7670] text-sm font-sans">{badge.description}</p>
                </div>
                {!badge.earned && (
                  <div className="text-right">
                    <p className="text-[#8BAA9E] text-sm font-sans">{Math.round(badge.progress)}%</p>
                  </div>
                )}
                {badge.earned && (
                  <div className="w-8 h-8 rounded-full bg-[#C6A75E]/20 flex items-center justify-center">
                    <span className="text-[#C6A75E]">✓</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav
        className="fixed left-4 right-4 bg-[#1C1C1E]/90 backdrop-blur-xl border border-white/10 rounded-full h-16 flex items-center justify-around px-6 shadow-2xl z-50"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
          data-testid="nav-home"
        >
          <Leaf className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-sans">Home</span>
        </button>
        <button 
          className="flex flex-col items-center gap-1 text-[#8BAA9E]"
          data-testid="nav-progress"
        >
          <BarChart3 className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-sans">Progress</span>
        </button>
        <button 
          onClick={() => navigate('/settings')}
          className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB]"
          data-testid="nav-settings"
        >
          <Settings className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-sans">Settings</span>
        </button>
      </nav>
    </div>
  );
};

export default Progress;
